import type { paths } from '@forgd/openapi/api'
import { useMutation } from '@pinia/colada'
import { defu } from 'defu'

import { useDevLogger } from './useDevLogger'
// workaround for issue importing tracing type from contract
type TracingEventType = NonNullable<paths['/events']['post']['requestBody']>['content']['application/json'][number]

const MaxQueueSize = 10

const logger = useDevLogger('tracer')

function createTracer(options?: { defaultEventData?: TracingEventType['eventData'] }) {
  const { public: { portal } } = useRuntimeConfig()
  const defaults = defu(options?.defaultEventData || {}, {
    // navigator is not available during SSR
    userAgent: import.meta.server ? '' : navigator.userAgent,
    portal,
  })
  const auth = useAuth()
  const route = useRoute()

  const { mutateAsync: sendEvents } = useMutation(
    buildApiMutation('/events', { method: 'post' }),
  )

  const queue: TracingEventType[] = []
  let flushQueued: boolean = false

  async function flush() {
    const body = [...queue.splice(0, MaxQueueSize)]
      .map(event => defu(event, { eventData: defaults }))
    if (import.meta.dev) {
      logger.debug('Flushing queue', body)
    }
    else if (body.length) {
      await sendEvents({ body })
    }
    flushQueued = false
    if (queue.length > 0) {
      queueFlush()
    }
  }

  function queueFlush() {
    if (!flushQueued) {
      flushQueued = true
      onNuxtReady(() => {
        flush()
      })
    }
  }

  return {
    pageView(data?: TracingEventType['eventData']) {
      if (!auth.isLoggedIn) {
        return
      }
      const payload = defu(
        { eventData: data },
        { eventData: { path: route.fullPath }, projectId: auth.project?.id, eventType: 'page_view' },
      )
      logger.debug('Page view', payload)
      queue.push(payload)
      queueFlush()
    },
    custom(eventType: string, data: TracingEventType['eventData']) {
      if (!auth.isLoggedIn) {
        return
      }
      const payload = defu(
        { eventData: data },
        { eventData: { path: route.fullPath }, projectId: auth.project?.id, eventType },
      )
      logger.debug(`Custom: ${eventType}`, payload)
      queue.push(payload)
      queueFlush()
    },
    identify(id: string, { email }: { email?: string }) {
      logger.debug('Identify user', { id, email })
    },
    reset() {
      logger.debug('Reset analytics')
    },
  }
}

type Tracer = ReturnType<typeof createTracer>

export function useTracer(): Tracer {
  const nuxtApp = useNuxtApp()
  // singleton
  if (!nuxtApp._tracer) {
    nuxtApp._tracer = createTracer()
  }
  return nuxtApp._tracer as Tracer
}
